import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "./api";

const PLATFORM_PREFIX = "platform_status.";
export const vkey = (f) => f.replace(/\./g, "__");

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(", ");
  return fallback;
};

/**
 * Loads one central video record and edits it field-by-field with autosave.
 * Both Catalog and Member Areas use this hook against the same /api/video-central/{id} document,
 * so an edit on either surface is the same write. Concurrent edits to one field surface as `conflicts`.
 *
 * status: "synced" | "unsaved" | "saving" | "conflict"
 */
export function useVideoRecord(videoId, source, { autosave = true, autosaveMs = 2000, pollMs = 15000 } = {}) {
  const [record, setRecord] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState(null);
  const baseVersions = useRef({});
  const draftRef = useRef(draft);
  const recordRef = useRef(record);
  const savingRef = useRef(false);
  const timer = useRef(null);
  draftRef.current = draft;
  recordRef.current = record;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api.get(`/api/video-central/${videoId}`);
      setRecord(r.data);
    } catch (e) {
      setLoadError(errText(e, "Could not load this video"));
    } finally {
      setLoading(false);
    }
  }, [videoId]);

  useEffect(() => {
    setDraft({});
    setConflicts(null);
    baseVersions.current = {};
    if (videoId) load();
    return () => clearTimeout(timer.current);
  }, [videoId, load]);

  // Pull remote edits (made from the other surface) into the open editor.
  useEffect(() => {
    if (!videoId || !pollMs) return undefined;
    const id = setInterval(async () => {
      if (savingRef.current || document.hidden) return;
      try {
        const r = await api.get(`/api/video-central/${videoId}`);
        const cur = recordRef.current;
        if (!cur || JSON.stringify(r.data.field_versions) !== JSON.stringify(cur.field_versions) || JSON.stringify(r.data.platform_captions) !== JSON.stringify(cur.platform_captions)) {
          setRecord(r.data);
        }
      } catch { /* transient */ }
    }, pollMs);
    return () => clearInterval(id);
  }, [videoId, pollMs]);

  const merged = useMemo(() => {
    if (!record) return null;
    const out = { ...record };
    const ps = { ...(record.platform_status || {}) };
    Object.entries(draft).forEach(([f, v]) => {
      if (f.startsWith(PLATFORM_PREFIX)) ps[f.slice(PLATFORM_PREFIX.length)] = { ...(ps[f.slice(PLATFORM_PREFIX.length)] || {}), ...v };
      else out[f] = v;
    });
    out.platform_status = ps;
    return out;
  }, [record, draft]);

  const save = useCallback(async ({ force = [], quiet = false } = {}) => {
    const changes = draftRef.current;
    const fields = Object.keys(changes);
    if (!fields.length || savingRef.current) return true;
    savingRef.current = true;
    setSaving(true);
    try {
      const r = await api.patch(`/api/video-central/${videoId}`, {
        changes, base_versions: baseVersions.current, force, source,
      });
      setRecord(r.data);
      setDraft((d) => {
        const next = { ...d };
        fields.forEach((f) => { if (next[f] === changes[f]) { delete next[f]; delete baseVersions.current[f]; } });
        return next;
      });
      setConflicts(null);
      toast.success(quiet ? "Saved" : "Changes saved and synced", { id: "video-central-save" });
      window.dispatchEvent(new CustomEvent("gio:data-changed", { detail: { resourceType: "videos" } }));
      return true;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "sync_conflict") {
        setConflicts(detail.conflicts);
        toast.error("Sync conflict: the same field was changed elsewhere", { id: "video-central-save" });
      } else {
        toast.error(errText(e, "Save failed. Your changes are kept; try again."), { id: "video-central-save" });
      }
      return false;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [videoId, source]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (!autosave || conflicts || !Object.keys(draft).length) return undefined;
    timer.current = setTimeout(() => save({ quiet: true }), autosaveMs);
    return () => clearTimeout(timer.current);
  }, [draft, autosave, autosaveMs, conflicts, save]);

  const setField = useCallback((field, value) => {
    if (!(field in draftRef.current)) {
      baseVersions.current[field] = recordRef.current?.field_versions?.[vkey(field)] ?? null;
    }
    setDraft((d) => ({ ...d, [field]: value }));
  }, []);

  const setPlatform = useCallback((key, patch) => {
    const field = `${PLATFORM_PREFIX}${key}`;
    const current = draftRef.current[field] || {};
    setField(field, { ...current, ...patch });
  }, [setField]);

  const discard = useCallback(() => {
    setDraft({});
    baseVersions.current = {};
    setConflicts(null);
  }, []);

  // choice: "mine" keeps the local edit (force overwrite); "theirs" drops it and keeps the stored value.
  const resolveConflicts = useCallback(async (choices) => {
    const mine = Object.entries(choices).filter(([, c]) => c === "mine").map(([f]) => f);
    const theirs = Object.entries(choices).filter(([, c]) => c === "theirs").map(([f]) => f);
    const fresh = await api.get(`/api/video-central/${videoId}`).then((r) => r.data).catch(() => null);
    if (fresh) setRecord(fresh);
    theirs.forEach((f) => { delete baseVersions.current[f]; });
    mine.forEach((f) => { baseVersions.current[f] = fresh?.field_versions?.[vkey(f)] ?? null; });
    setDraft((d) => {
      const next = { ...d };
      theirs.forEach((f) => delete next[f]);
      draftRef.current = next;
      return next;
    });
    setConflicts(null);
    if (mine.length) await save({ force: mine });
    else toast.success("Kept the stored version", { id: "video-central-save" });
  }, [videoId, save]);

  const dirty = Object.keys(draft).length > 0;
  const status = conflicts ? "conflict" : saving ? "saving" : dirty ? "unsaved" : "synced";

  return { record: merged, serverRecord: record, loading, loadError, status, dirty, conflicts, saving, setField, setPlatform, save, discard, resolveConflicts, reload: load, draft };
}
